import { useParams } from "react-router";
import React, { useEffect, useState } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import { ThunkDispatch } from "core-integration/src/store";
import { updateElementControls } from "core-integration/src/store/elements/actions";
import {
  getElementControls,
  getElementsFromControls,
} from "core-integration/src/store/elements/reducers";
import { updateElementControlsAndMaxCost } from "core-integration/src/store/elements/thunks";
import { IElement } from "core-integration/src/store/elements/types";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { integerToMoney } from "core-integration/src/utils/money";
import { getStatDetails } from "../../utils/stats";
import Alert from "../Alert";
import ElementFilter from "../element-controls/ElementFilter";
import ElementSort from "../element-controls/ElementSort";
import Paginator, { paginate } from "../element-controls/Paginator";
import ElementDialogButton from "../ElementDialogButton";
import ElementInTable from "../ElementInTable";
import { ElementRow, ElementTable } from "../ElementTable";
import { FieldCol, FieldCols } from "../FieldRenderers";
import { Main, Wrapper } from "../Layout";
import Panel from "../Panel";
import Title from "../Title";
import Tooltip, { TooltipLabel } from "../Tooltip";

const StatsStatus = styled.th`
  width: 10%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 7%;
  }
`;

const StatsElement = styled.th`
  width: 32%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 38%;
  }
`;

const StatsCost = styled.th`
  width: 14%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsSel = styled.th`
  width: 11%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsForm = styled.th`
  width: 13%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsPts = styled.th`
  width: 10%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsExtra = styled.th`
  width: 10%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

type Props = WithTranslation;

const Statistics = ({ t }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const { statName } = useParams();
  const [page, setPage] = useState(1);

  const controls = useSelector(getElementControls);
  const currencyDivisor = 10;
  const elements = useSelector(getElementsFromControls);
  const teamsById = useSelector(getTeamsById);

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPage(1);
    dispatch(
      updateElementControls({
        ...controls,
        filter: e.target.value,
      }),
    );
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPage(1);
    dispatch(
      updateElementControls({
        ...controls,
        sort: e.target.value,
      }),
    );
  };

  const defaultStats: Array<keyof IElement> = [
    "now_cost",
    "selected_by_percent",
    "form",
    "total_points",
  ];

  useEffect(() => {
    dispatch(
      updateElementControlsAndMaxCost({
        ...controls,
        filter: "all",
        sort: statName || "total_points",
        search: "",
      }),
    );
  }, []);

  const { data, totalPages } = paginate(elements.data, page, 30);
  const extraStat =
    defaultStats.indexOf(controls.sort) === -1 ? controls.sort : "";
  const statDetail = getStatDetails(String(controls.sort), t);

  return (
    <Wrapper>
      <Main>
        <Box mx={2}>
          <Title>{t("statistics.title", "Statistics")}</Title>
        </Box>
        <form>
          <FieldCols>
            <FieldCol>
              <ElementFilter handleFilterChange={handleFilterChange} />
            </FieldCol>
            <FieldCol>
              <ElementSort handleSortChange={handleSortChange} />
            </FieldCol>
          </FieldCols>
        </form>
        <Box mb="4">
          <Alert type="info" textAlign="left">
            {statDetail ? statDetail.description : ""}
          </Alert>
        </Box>
        <Panel>
          <ElementTable>
            <thead>
              <tr>
                <StatsStatus>&nbsp;</StatsStatus>
                <StatsElement>{t("statistics.player", "Player")}</StatsElement>
                <StatsCost>mNOK</StatsCost>
                <StatsSel>
                  <Tooltip
                    content={t("statistics.selectedByLong", "Selected by %")}
                  >
                    <TooltipLabel>
                      {t("statistics.selectedByShort", "Sel.")}
                    </TooltipLabel>
                  </Tooltip>
                </StatsSel>
                <StatsForm>{t("statistics.form", "Form")}</StatsForm>
                <StatsPts>
                  <Tooltip
                    content={t("statistics.totalPoints", "Total points")}
                  >
                    <TooltipLabel>
                      {t("statistics.totalPointsShort", "Pts.")}
                    </TooltipLabel>
                  </Tooltip>
                </StatsPts>
                {extraStat ? <StatsExtra>**</StatsExtra> : null}
              </tr>
            </thead>
            <tbody>
              {data.map((e) => (
                <ElementRow key={e.id}>
                  <td>
                    <ElementDialogButton elementId={e.id} variant="list" />
                  </td>
                  <td>
                    <ElementInTable
                      renderElementMenu={() => {
                        window.console.log("not needed");
                      }}
                      element={e}
                      team={teamsById[e.team]}
                    />
                  </td>
                  <td>{integerToMoney(e.now_cost, currencyDivisor)}</td>
                  <td>{e.selected_by_percent}%</td>
                  <td>{e.form}</td>
                  <td>{e.total_points}</td>
                  {extraStat ? <td>{e[extraStat]}</td> : null}
                </ElementRow>
              ))}
            </tbody>
          </ElementTable>
        </Panel>
        <Paginator totalPages={totalPages} page={page} setPage={setPage} />
      </Main>
    </Wrapper>
  );
};

export default withTranslation()(Statistics);
